import { Component, EventEmitter, Input, OnChanges, Output, ViewChild, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CatalogWarehouse, InventoryLedgerEntry, InventoryStockBalance } from './catalog.service';
import { OfferingInventoryMovementsPanelComponent } from './offering-inventory-movements-panel.component';
import { OfferingInventoryLotsPanelComponent } from './offering-inventory-lots-panel.component';
import { OfferingInventoryCostingPanelComponent } from './offering-inventory-costing-panel.component';
import { OfferingInventoryReservationsPanelComponent } from './offering-inventory-reservations-panel.component';

export type OfferingInventorySubTab = 'balances' | 'ledger' | 'movements' | 'lots' | 'costing' | 'reservations';

@Component({
  standalone: true,
  selector: 'ain-offering-inventory-workspace',
  imports: [
    InfoHintComponent,
    ButtonModule,
    TableModule,
    OfferingInventoryMovementsPanelComponent,
    OfferingInventoryLotsPanelComponent,
    OfferingInventoryCostingPanelComponent,
    OfferingInventoryReservationsPanelComponent
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.offerings.inventory.hint.title'"
      [descriptionKey]="'catalog.offerings.inventory.hint.description'" />

    <section class="subtabs">
      @for (tab of subTabs; track tab.key) {
        @if (tab.visible) {
          <button type="button" [class.active]="activeSubTab() === tab.key" (click)="selectSubTab(tab.key)">
            {{ t(tab.labelKey) }}
          </button>
        }
      }
    </section>

    @if (activeSubTab() === 'balances') {
      <div class="tab-toolbar">
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="onInventoryChanged()"></button>
        <button pButton type="button" [label]="t('catalog.offerings.inventory.quickMovement')" (click)="goToMovements()"></button>
      </div>
      <p-table [value]="balances">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('catalog.inventory.warehouseId') }}</th>
            <th>{{ t('catalog.inventory.binId') }}</th>
            <th>{{ t('catalog.inventory.quantity') }}</th>
            <th>{{ t('catalog.inventory.lastUpdated') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ resolveWarehouseLabel(row.warehouseId) }}</td>
            <td>{{ row.binId || '-' }}</td>
            <td>{{ row.quantity }}</td>
            <td>{{ row.lastUpdatedUtc }}</td>
          </tr>
        </ng-template>
      </p-table>
    }

    @if (activeSubTab() === 'ledger') {
      <div class="tab-toolbar">
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="onInventoryChanged()"></button>
      </div>
      <p-table [value]="ledger">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('catalog.inventory.operation') }}</th>
            <th>{{ t('catalog.inventory.reference') }}</th>
            <th>{{ t('catalog.inventory.delta') }}</th>
            <th>{{ t('catalog.inventory.quantityAfter') }}</th>
            <th>{{ t('catalog.inventory.occurredAt') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.operationType }}</td>
            <td>{{ row.referenceNumber }}</td>
            <td>{{ row.quantityDelta }}</td>
            <td>{{ row.quantityAfter }}</td>
            <td>{{ row.occurredAtUtc }}</td>
          </tr>
        </ng-template>
      </p-table>
    }

    @if (activeSubTab() === 'movements') {
      <ain-offering-inventory-movements-panel
        #movementsPanel
        [offeringId]="offeringId"
        (inventoryChanged)="onInventoryChanged()" />
    }
    @if (activeSubTab() === 'lots') {
      <ain-offering-inventory-lots-panel [offeringId]="offeringId" (inventoryChanged)="onInventoryChanged()" />
    }
    @if (activeSubTab() === 'costing') {
      <ain-offering-inventory-costing-panel [offeringId]="offeringId" (inventoryChanged)="onInventoryChanged()" />
    }
    @if (activeSubTab() === 'reservations') {
      <ain-offering-inventory-reservations-panel [offeringId]="offeringId" (inventoryChanged)="onInventoryChanged()" />
    }
  `,
  styles: [`
    .subtabs { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
    .subtabs button { border: 1px solid #cbd5e1; border-radius: 8px; background: #f8fafc; padding: 6px 12px; cursor: pointer; font-size: 0.875rem; }
    .subtabs button.active { border-color: #0f766e; color: #0f766e; background: #ecfdf5; font-weight: 700; }
    .tab-toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
  `]
})
export class OfferingInventoryWorkspaceComponent implements OnChanges {
  @Input({ required: true }) offeringId!: string;
  @Output() inventoryChanged = new EventEmitter<void>();
  @Input() balances: InventoryStockBalance[] = [];
  @Input() ledger: InventoryLedgerEntry[] = [];
  @Input() warehouses: CatalogWarehouse[] = [];
  @Input() initialSubTab: OfferingInventorySubTab = 'balances';
  @Input() showMovements = true;
  @Input() showLots = true;
  @Input() showCosting = true;
  @Input() showReservations = true;

  ngOnChanges(): void {
    if (this.initialSubTab) {
      this.activeSubTab.set(this.initialSubTab);
    }
  }

  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly activeSubTab = signal<OfferingInventorySubTab>('balances');

  @ViewChild('movementsPanel') private movementsPanel?: OfferingInventoryMovementsPanelComponent;

  private pendingOpenMovementDialog = false;

  get subTabs(): Array<{ key: OfferingInventorySubTab; labelKey: string; visible: boolean }> {
    return [
      { key: 'balances', labelKey: 'catalog.tabs.balances', visible: true },
      { key: 'ledger', labelKey: 'catalog.tabs.ledger', visible: true },
      { key: 'movements', labelKey: 'catalog.offerings.inventory.subtabs.movements', visible: this.showMovements },
      { key: 'lots', labelKey: 'catalog.offerings.inventory.subtabs.lots', visible: this.showLots },
      { key: 'costing', labelKey: 'catalog.offerings.inventory.subtabs.costing', visible: this.showCosting },
      { key: 'reservations', labelKey: 'catalog.offerings.inventory.subtabs.reservations', visible: this.showReservations }
    ];
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) {
      return '-';
    }
    const warehouse = this.warehouses.find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : warehouseId;
  }

  selectSubTab(key: OfferingInventorySubTab): void {
    this.activeSubTab.set(key);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'inventory', inventoryTab: key },
      queryParamsHandling: 'merge'
    });
  }

  setSubTab(key: OfferingInventorySubTab): void {
    this.activeSubTab.set(key);
  }

  goToMovements(): void {
    if (this.activeSubTab() === 'movements') {
      this.movementsPanel?.openOperationDialog();
      return;
    }
    this.pendingOpenMovementDialog = true;
    this.selectSubTab('movements');
    queueMicrotask(() => {
      if (!this.pendingOpenMovementDialog) {
        return;
      }
      this.pendingOpenMovementDialog = false;
      this.movementsPanel?.openOperationDialog();
    });
  }

  onInventoryChanged(): void {
    this.inventoryChanged.emit();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
